import { jsx as _jsx } from "react/jsx-runtime";
import isUndefined from "lodash/isUndefined";
import noop from "lodash/noop";
import { useRouter } from "next/router";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { usePrevious } from "__barrel_optimize__?names=usePrevious!=!react-use";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { isDevOrTestStage } from "~/swr-hooks/utils";
import { reportErrorToBugsnag } from "~/utils/ErrorUtils";
const DEFAULT_VALUE = {
    hasNewRelease: false,
    isScheduled: false,
    pendingActions: [],
    onAddPendingAction: noop,
    onDismiss: noop,
    onReload: noop,
    onRemovePendingAction: noop,
    onScheduleReload: noop
};
export const ReloadApplicationContext = /*#__PURE__*/ createContext(DEFAULT_VALUE);
export const ReloadApplicationProvider = (param)=>{
    let { children } = param;
    const { forceReload: forceReloadFlag, releaseVersion } = useFeatureFlags();
    const [forceReload, setForceReload] = useState(false);
    const router = useRouter();
    const [isScheduled, setIsScheduled] = useState(false);
    const [hasNewRelease, setHasNewRelease] = useState(false);
    const [pendingActions, setPendingActions] = useState([]);
    const previousRelease = usePrevious(releaseVersion);
    /**
   * This will force reload the application when there is a new
   * deployment and the `forceReload` flag is enabled.
   */ useEffect(()=>{
        if (forceReloadFlag && !forceReload && hasNewRelease) {
            setForceReload(true);
            router.reload();
        }
    }, [
        forceReload,
        forceReloadFlag,
        hasNewRelease,
        setForceReload,
        router
    ]);
    /**
   * This will set `hasNewDeployment` to true when there is a new
   * deployment is detected.
   */ useEffect(()=>{
        if (releaseVersion && !isUndefined(previousRelease) && releaseVersion !== previousRelease) {
            setHasNewRelease(true);
        }
    }, [
        previousRelease,
        releaseVersion
    ]);
    /**
   * This will reload the application when there is a new deployment
   * and the `isScheduled` flag is set to true.
   */ useEffect(()=>{
        if (isScheduled && pendingActions.length === 0) {
            setIsScheduled(false);
            router.reload();
        }
    }, [
        isScheduled,
        pendingActions,
        router,
        setIsScheduled
    ]);
    const onAddPendingAction = useCallback((newPendingAction)=>{
        return setPendingActions((prev)=>[
                ...prev,
                newPendingAction
            ]);
    }, [
        setPendingActions
    ]);
    const onDismiss = useCallback(()=>{
        setHasNewRelease(false);
    }, [
        setHasNewRelease
    ]);
    const onRemovePendingAction = useCallback((pendingActionId)=>{
        setPendingActions((prev)=>prev.filter((pendingAction)=>pendingAction.id !== pendingActionId));
    }, [
        setPendingActions
    ]);
    const onReload = useCallback(()=>{
        router.reload();
    }, [
        router
    ]);
    const onScheduleReload = useCallback(()=>{
        setIsScheduled(true);
    }, [
        setIsScheduled
    ]);
    const value = useMemo(()=>{
        return {
            hasNewRelease,
            isScheduled,
            pendingActions,
            onAddPendingAction,
            onDismiss,
            onReload,
            onRemovePendingAction,
            onScheduleReload
        };
    }, [
        hasNewRelease,
        isScheduled,
        onAddPendingAction,
        onDismiss,
        onReload,
        onRemovePendingAction,
        onScheduleReload,
        pendingActions
    ]);
    return /*#__PURE__*/ _jsx(ReloadApplicationContext.Provider, {
        value: value,
        children: children
    });
};
export const useReloadApplication = ()=>{
    const context = useContext(ReloadApplicationContext);
    if (context === DEFAULT_VALUE) {
        const error = "ReloadApplicationContext used outside of ReloadApplicationProvider";
        if (isDevOrTestStage()) {
            throw new Error(error);
        } else {
            reportErrorToBugsnag({
                error,
                context: error
            });
        }
    }
    return context;
};
